import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ChevronLeft, Send, Headphones } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { RiderShell, DriverShell } from "@/components/Shells";
import { cn } from "@/lib/format";

export default function Chat() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);
  const isSupport = id?.startsWith("support-");

  const load = () => api(`/rides/${id}/messages`).then((r) => setMessages(r.messages || [])).catch(() => {});
  useEffect(() => {
    load();
    const iv = setInterval(load, 3000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async () => {
    const t = text.trim();
    if (!t) return;
    setText("");
    setSending(true);
    setMessages((m) => [...m, { id: `tmp-${Date.now()}`, sender_role: user.role, text: t, at: Date.now() / 1000 }]);
    try {
      await api(`/rides/${id}/messages`, { method: "POST", body: { text: t } });
      await load();
    } catch { /* noop */ } finally { setSending(false); }
  };

  const Shell = user?.role === "customer" ? RiderShell : DriverShell;

  const fmtMsgTime = (at) => {
    if (!at) return "";
    const d = new Date(at * 1000);
    const now = new Date();
    const time = d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
    return d.toDateString() === now.toDateString()
      ? time
      : `${d.toLocaleDateString([], { month: "short", day: "numeric" })} · ${time}`;
  };

  return (
    <Shell fullBleed>
      <div className="flex h-full flex-col bg-background">
      <header className="flex shrink-0 items-center gap-3 border-b border-border bg-card px-4 py-3">
        <button onClick={() => navigate(-1)} className="flex h-9 w-9 items-center justify-center rounded-full bg-secondary text-foreground">
          <ChevronLeft className="h-5 w-5" />
        </button>
        <span className="flex h-9 w-9 items-center justify-center rounded-full bg-accent text-brand-600">
          <Headphones className="h-4 w-4" />
        </span>
        <div>
          <p className="font-bold text-foreground">{isSupport ? "Getaride Support" : "Trip chat"}</p>
          <p className="text-xs text-muted-foreground">{isSupport ? "We usually reply in a few minutes" : "Messages about your trip"}</p>
        </div>
      </header>

      <div className="mx-auto w-full max-w-2xl shrink-0 border-b border-border bg-card p-3">
        <div className="flex items-center gap-2">
          <input
            data-testid="chat-input"
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && send()}
            placeholder="Type a message…"
            className="h-12 flex-1 rounded-full border border-border bg-secondary px-4 text-base text-foreground outline-none focus:border-brand-500"
          />
          <button onClick={send} disabled={sending || !text.trim()} data-testid="chat-send" className="flex h-12 w-12 items-center justify-center rounded-full bg-brand-600 text-white disabled:opacity-50">
            <Send className="h-5 w-5" />
          </button>
        </div>
      </div>

      <div className="mx-auto flex w-full max-w-2xl flex-1 flex-col gap-2 overflow-y-auto p-4">
        {messages.map((m) => {
          const mine = m.sender_role === user.role && m.sender_role !== "support";
          return (
            <div key={m.id} className={cn("flex flex-col", mine ? "items-end" : "items-start")}>
              <div className={cn("max-w-[78%] rounded-2xl px-4 py-2.5 text-sm", mine ? "rounded-br-md bg-brand-600 text-white" : "rounded-bl-md bg-secondary text-foreground")}>
                {!mine && m.sender_name && <p className="mb-0.5 text-xs font-semibold opacity-70">{m.sender_name}</p>}
                {m.text}
              </div>
              <span className="mt-0.5 px-1 text-[10px] text-muted-foreground">{fmtMsgTime(m.at)}</span>
            </div>
          );
        })}
        <div ref={endRef} />
      </div>
      </div>
    </Shell>
  );
}
